import { ChevronLeft, ChevronRight } from 'react-feather';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterIndicator, StatBar } from './Layout';
import { assetUrl } from '../../common/helpers';
import { useColors } from './helpers';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';

interface Item {
  count: number;
}

export interface OriginListProps<T extends Item> {
  title: string;
  description?: string;
  items: T[];
  itemsCount: number;
  totalCount: number;
  renderItem: (item: T) => ReactNode;
  image?: string;
}

function OriginList<T extends Item>({
  title, description, items, itemsCount, totalCount, renderItem, image = 'dashboard-countries.svg',
}: OriginListProps<T>) {
  const { t } = useTranslation();
  const { formatInteger, formatNumber } = useLocale();

  const formatPercentage = (number: number) => (
    `${formatNumber(number, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
  );

  const colors = useColors(-100);

  const perPage = 5;
  const [limit, setLimit] = useState(perPage);

  // Render all items up to the limit + the next page, to ensure a smooth animation
  const visibleItems = items.slice(0, limit + perPage);

  const next = () => setLimit((limit) => limit + perPage);
  const prev = () => setLimit((limit) => limit - perPage);

  return (
    <UI.Card sc={{ hasPanels: true }}>
      <UI.GridContainer sc={{ gutter: 0.75 }} style={{ height: '100%' }}>
        <UI.Div>
          {/* Put the title and data in their own container to keep them together when there are less than 5 items */}
          <UI.FlexContainer sc={{ alignItems: 'center' }}>
            <UI.H4>
              {title}
              <UI.Span sc={{ muted: true, fontWeight: 400 }}>
                {/* Response always contains at most 100 items */}
                {` (${items.length >= 250 ? '250+' : formatInteger(itemsCount)})`}
              </UI.Span>
            </UI.H4>
            <FilterIndicator sc={{ ml: 2 }} />
          </UI.FlexContainer>

          {description && (
            <UI.Div sc={{ color: 'gray.500' }}>
              {description}
            </UI.Div>
          )}

          <UI.Div sc={{ mt: 3 }} style={{ height: Math.min(items.length, perPage) * 28, overflow: 'hidden' }}>
            <UI.GridContainer
              sc={{ gutter: 0 }}
              style={{
                marginTop: -(limit - perPage) * 28,
                transition: 'margin 0.15s ease-in-out',
              }}
            >
              {visibleItems.map((item, index) => (
                <UI.GridContainer
                  sc={{ columns: '1fr 75px 48px', gutter: 0, alignVertical: 'center' }}
                  key={index}
                >
                  <UI.Div sc={{ color: 'gray.800', pr: 2 }} style={{ minWidth: 0 }}>
                    <StatBar values={[item.count]} max={totalCount} colors={[colors[0]]}>
                      <UI.Truncate>
                        {renderItem(item)}
                      </UI.Truncate>
                    </StatBar>
                  </UI.Div>
                  <UI.Div>
                    {formatInteger(item.count)}
                  </UI.Div>
                  <UI.Div sc={{ muted: true, small: true }}>
                    {totalCount > 0 && formatPercentage((item.count / totalCount) * 100)}
                  </UI.Div>
                </UI.GridContainer>
              ))}
            </UI.GridContainer>
          </UI.Div>
        </UI.Div>

        {(limit < items.length || limit > perPage) && (
          <UI.Div style={{ marginTop: 'auto' }}>
            <UI.Button
              onClick={() => prev()}
              sc={{ blank: true, size: 'xs' }}
              disabled={limit <= perPage}
            >
              <UI.Icon sc={{ muted: true }}>
                <ChevronLeft />
              </UI.Icon>
              {' '}
              {t('previous')}
            </UI.Button>
            {' '}
            <UI.Button
              onClick={() => next()}
              sc={{ blank: true, size: 'xs', mr: 2 }}
              disabled={limit >= items.length}
            >
              {t('next')}
              {' '}
              <UI.Icon sc={{ muted: true }}>
                <ChevronRight />
              </UI.Icon>
            </UI.Button>
          </UI.Div>
        )}

        {items.length === 0 && (
          <UI.Div>
            <UI.FlexContainer sc={{ justifyContent: 'center', mb: 2 }}>
              <img src={assetUrl(`images/${image}`)} height={60} alt={title} />
            </UI.FlexContainer>
            <UI.Div sc={{ textAlign: 'center', color: 'gray.400' }}>
              {t('no_data_available')}
            </UI.Div>
          </UI.Div>
        )}
      </UI.GridContainer>
    </UI.Card>
  );
}

export default OriginList;
